import { Injectable } from '@angular/core';
import { getJawByToothNumber } from '@modules/teeth-diagram/models/teeth-numbering';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { bridgeUnitType } from '@shared/models/consts';
import { RxIntuitiveBridgeService } from '../rx-intuitive-bridge-service';
import { RxIntuitiveCopyFromBehaviorService } from '../rx-intuitive-copy-from-behavior.service';
import { RxIntuitiveIndicationMapperService } from '../rx-intuitive-indication-mapper.service';
import { RxIntuitiveTeethDiagramQuery } from '../../state/rx-intuitive-teeth-diagram-query';
import { RxIntuitiveTeethDiagramStore } from '../../state/rx-intuitive-teeth-diagram-store';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { IntuitiveBridge } from '../../models/intuitive-bridge';

/**
 * Owns all write / mutation operations for the teeth diagram.
 *
 * Both {@link RxIntuitiveTeethDiagramFacade} and
 * {@link RxIntuitiveTeethDiagramBridgeFacade} compose this service so that
 * store mutations are not duplicated across the two thin facades.
 */
@Injectable()
export class RxIntuitiveTeethDiagramMutationService {
	constructor(
		private readonly bridgeService: RxIntuitiveBridgeService,
		private readonly intuitiveStore: RxIntuitiveTeethDiagramStore,
		private readonly intuitiveQuery: RxIntuitiveTeethDiagramQuery,
		private readonly teethDiagramQuery: TeethDiagramQuery,
		private readonly copyFromBehaviorService: RxIntuitiveCopyFromBehaviorService,
		private readonly indicationMapper: RxIntuitiveIndicationMapperService
	) {}

	/**
	 * Creates a new bridge span from the given tooth IDs, selects it for
	 * editing, and marks all bridge teeth as recently edited.
	 */
	createBridge(selectedToothIds: number[]): void {
		const bridge = this.bridgeService.createBridgeTeethSpan(selectedToothIds, this.teethDiagramQuery.teeth);

		if (bridge) {
			this.selectBridge(bridge);
			this.markRecentlyEditedTeeth(bridge.teeth.map(t => t.ToothID).filter((id): id is number => id != null));
		}
	}

	/**
	 * Handles a tooth click in the standard (non-bridge-editing) context.
	 *
	 * - If the tooth belongs to a bridge and the user is not already in bridge
	 *   mode, enters bridge mode focused on that tooth.
	 * - Otherwise applies exclusive-vs-multi-select logic based on indication
	 *   type and copy-from eligibility.
	 */
	toggleToothSelection(toothId: number): void {
		const teeth = this.teethDiagramQuery.teethRecord;
		const selectedToothIds = this.intuitiveQuery.selectedToothIds;
		const tooth = teeth[toothId];

		if (tooth?.BridgeIndex) {
			const isBridgeMode = !!this.intuitiveQuery.selectedBridge;
			const bridge = this.bridgeService.getBridgeSpanByBridgeIndex(tooth.BridgeIndex, this.teethDiagramQuery.teethFlat);

			if (bridge && !isBridgeMode) {
				return this.intuitiveStore.selectBridge(bridge, toothId);
			}
		}

		let next: number[];

		if (selectedToothIds.includes(toothId)) {
			next = selectedToothIds.filter(id => id !== toothId);
		} else if (
			!this.indicationMapper.isDefaultUnitType(tooth?.UnitTypeID) ||
			(tooth != null && this.copyFromBehaviorService.isCopyFromSourceTooth(tooth))
		) {
			next = [toothId];
		} else {
			next = selectedToothIds.filter(id => this.indicationMapper.isDefaultUnitType(teeth[id]?.UnitTypeID));
			next = [...next, toothId];
		}

		this.updateSelectedToothIds(next);
	}

	/**
	 * Clears the current tooth selection.
	 */
	clearToothSelection(): void {
		this.updateSelectedToothIds([]);
	}

	/**
	 * Delegates copy-from-tooth to the copy-from behavior service.
	 */
	copyTreatmentDetailsFromSourceTooth(sourceToothId: number): void {
		this.copyFromBehaviorService.copyTreatmentDetailsFromSourceTooth(sourceToothId);
	}

	/**
	 * Marks the given tooth IDs as recently edited in the intuitive diagram
	 * store (updates `toothLastTouchedAt` timestamps).
	 */
	markRecentlyEditedTeeth(toothIds: number[]): void {
		this.intuitiveStore.markRecentlyEditedTeeth(toothIds);
	}

	/**
	 * Enters bridge-editing mode for the given bridge, optionally focusing on
	 * a specific tooth within it.
	 */
	selectBridge(bridge: IntuitiveBridge, focusOnTooth?: number): void {
		this.intuitiveStore.selectBridge(bridge, focusOnTooth);
	}

	/**
	 * Updates the set of currently selected tooth IDs in the intuitive store.
	 */
	updateSelectedToothIds(toothIds: number[]): void {
		this.intuitiveStore.update({ selectedToothIds: toothIds });
	}

	// ─── Bridge-editing helpers (used by RxIntuitiveTeethDiagramBridgeFacade) ──

	/**
	 * Attempts to extend the currently selected bridge by adding a tooth
	 * adjacent to its span.  No-op if the tooth belongs to a different bridge,
	 * there is no anchor tooth in the bridge, or the bridge cannot be extended
	 * in that direction.
	 */
	addTeethInBridge(toothId: number, selectedBridge: IntuitiveBridge): void {
		const tooth = this.teethDiagramQuery.teethRecord[toothId];

		if (tooth?.BridgeIndex != null && tooth.BridgeIndex !== selectedBridge.index) {
			return;
		}

		const bridgeToothId = selectedBridge.outerLeftToothId ?? selectedBridge.outerRightToothId;

		if (bridgeToothId == null) {
			return;
		}

		const bridgeJaw = getJawByToothNumber({ toothId: bridgeToothId });
		const jaw = bridgeJaw === 'upper' ? this.teethDiagramQuery.teeth.upperJaw : this.teethDiagramQuery.teeth.lowerJaw;
		const teethToAdd = this.bridgeService.getTeethToExtendBridge(selectedBridge, toothId, jaw);

		if (!teethToAdd || teethToAdd.length === 0) {
			return;
		}

		const bridgeTeeth: Tooth[] = teethToAdd.map(t => ({
			...t,
			UnitTypeID: bridgeUnitType,
			BridgeIndex: selectedBridge.index
		}));

		const expandedBridge = this.bridgeService.buildExpandedBridge(selectedBridge, bridgeTeeth, jaw);
		const newToothIds = bridgeTeeth.map(t => t.ToothID).filter((id): id is number => id !== null);

		this.intuitiveStore.update({ selectedBridge: expandedBridge });
		this.markRecentlyEditedTeeth(newToothIds);
	}
}
